import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Star, 
  ShoppingCart, 
  Percent, 
  Clock, 
  Users, 
  Heart, 
  ExternalLink,
  Gift,
  Coins,
  Truck,
  Award,
  CheckCircle,
  Tag
} from 'lucide-react';
import { SponsorshipPartner, SponsoredProduct, SponsorCoupon } from '../../types/users';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';

interface SponsorshipIntegrationProps {
  context: 'recipe' | 'grocery_list' | 'meal_plan' | 'ingredient_search';
  contextData: {
    ingredients?: string[];
    recipeCategory?: string;
    userLocation?: string;
    dietaryPreferences?: string[];
    cookingSkill?: string;
  };
  onSponsorInteraction: (sponsorId: string, interactionType: string, data: any) => void;
  className?: string;
}

const SponsorshipIntegration: React.FC<SponsorshipIntegrationProps> = ({
  context,
  contextData,
  onSponsorInteraction,
  className = ''
}) => {
  const [relevantSponsors, setRelevantSponsors] = useState<SponsorshipPartner[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<SponsoredProduct[]>([]);
  const [availableCoupons, setAvailableCoupons] = useState<SponsorCoupon[]>([]);
  const [userInteractions, setUserInteractions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { getSponsorsNew, getSponsoredProductsNew, getCouponsNew, getInteractions, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('sponsorship-integration');

  // Filter sponsors based on context and user location
  const getContextualSponsors = (sponsors: SponsorshipPartner[]) => {
    return sponsors.filter(sponsor => {
      // Filter by location if provided
      if (contextData.userLocation) {
        const userZip = contextData.userLocation;
        if (!sponsor.serviceAreas.includes(userZip)) return false;
      }

      // Filter by context relevance
      if (context === 'recipe' && contextData.recipeCategory) {
        return sponsor.type === 'grocery_store' || sponsor.type === 'restaurant';
      }
      
      if (context === 'grocery_list') {
        return sponsor.type === 'grocery_store';
      }

      return true;
    });
  };

  // Get products relevant to current context
  const getContextualProducts = (products: SponsoredProduct[], sponsors: SponsorshipPartner[]) => {
    const relevantSponsorIds = sponsors.map(s => s.id);
    
    return products.filter(product => {
      if (!relevantSponsorIds.includes(product.sponsorId)) return false;
      
      // Filter by ingredients if in recipe context
      if (context === 'recipe' && contextData.ingredients) {
        const ingredientMatch = contextData.ingredients.some(ingredient => {
          // Ensure ingredient is a string
          const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name || '';
          return product.name.toLowerCase().includes(ingredientName.toLowerCase()) ||
                 product.description.toLowerCase().includes(ingredientName.toLowerCase());
        });
        if (ingredientMatch) return true;
      }

      // Filter by dietary preferences
      if (contextData.dietaryPreferences) {
        const dietMatch = contextData.dietaryPreferences.some(pref =>
          product.targetDietaryPreferences.includes(pref)
        );
        if (dietMatch) return true;
      }

      return false;
    });
  };

  // Get available coupons for context
  const getContextualCoupons = (coupons: SponsorCoupon[], sponsors: SponsorshipPartner[]) => {
    const relevantSponsorIds = sponsors.map(s => s.id);
    
    return coupons.filter(coupon => {
      if (!relevantSponsorIds.includes(coupon.sponsorId)) return false;
      if (!coupon.isActive) return false;
      
      // Check if coupon is valid
      const now = new Date();
      const validFrom = new Date(coupon.validFrom);
      const validUntil = new Date(coupon.validUntil);
      
      if (now < validFrom || now > validUntil) return false;

      // Check context-specific display settings
      if (context === 'recipe' && !coupon.showInRecipes) return false;
      if (context === 'grocery_list' && !coupon.showInGroceryLists) return false;
      if (context === 'meal_plan' && !coupon.showInMealPlans) return false;

      return true;
    });
  };

  useEffect(() => {
    setIsLoading(true);
    
    if (isExampleData) {
      // Load from centralized example data
      const exampleSponsors = getSponsorsNew();
      const exampleProducts = getSponsoredProductsNew();
      const exampleCoupons = getCouponsNew();
      const exampleInteractions = getInteractions();
      
      const contextualSponsors = getContextualSponsors(exampleSponsors as SponsorshipPartner[]);
      const contextualProducts = getContextualProducts(exampleProducts as SponsoredProduct[], contextualSponsors);
      const contextualCoupons = getContextualCoupons(exampleCoupons as SponsorCoupon[], contextualSponsors);
      
      setRelevantSponsors(contextualSponsors);
      setFeaturedProducts(contextualProducts);
      setAvailableCoupons(contextualCoupons);
      setUserInteractions(exampleInteractions);
    } else {
      // Load from real API
      // const apiSponsors = await fetchSponsorsFromAPI();
      // const apiProducts = await fetchSponsoredProductsFromAPI();
      // const apiCoupons = await fetchCouponsFromAPI();
      // const apiInteractions = await fetchInteractionsFromAPI();
      
      setRelevantSponsors([]);
      setFeaturedProducts([]);
      setAvailableCoupons([]);
      setUserInteractions([]);
    }
    
    setIsLoading(false);
  }, [context, contextData, isExampleData]);

  const handleSponsorInteraction = (sponsorId: string, interactionType: string, data: any = {}) => {
    // Track the interaction
    const interaction = {
      sponsorId,
      type: interactionType,
      timestamp: new Date().toISOString(),
      context,
      contextData,
      ...data
    };
    
    setUserInteractions(prev => [...prev, interaction]);
    
    // Call parent callback
    onSponsorInteraction(sponsorId, interactionType, {
      context,
      contextData,
      timestamp: new Date().toISOString(),
      ...data
    });
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="bg-gray-200 h-20 rounded-lg mb-3"></div>
        <div className="bg-gray-200 h-16 rounded-lg"></div>
      </div>
    );
  }

  if (relevantSponsors.length === 0 && featuredProducts.length === 0 && availableCoupons.length === 0) {
    return null; // Don't show anything if no relevant sponsors
  }

  const ProductSuggestion: React.FC<{ product: SponsoredProduct }> = ({ product }) => (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3 mb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Award className="w-5 h-5 text-green-600" />
          </div>
          
          <div>
            <div className="flex items-center space-x-2">
              <h5 className="font-medium text-gray-900">{product.name}</h5>
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                Community Favorite
              </span>
            </div>
            
            <p className="text-sm text-gray-600">{product.description}</p>
            
            <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Star className="w-3 h-3 fill-current text-yellow-500" />
                <span>{product.userRating} ({product.userReviewCount} reviews)</span>
              </div>
              
              <span className="font-medium text-green-600">${product.averagePrice}</span>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => handleSponsorInteraction(product.sponsorId, 'product_view', { productId: product.id })}
          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
        >
          View
        </button>
      </div>
    </div>
  );

  const CouponOffer: React.FC<{ coupon: SponsorCoupon }> = ({ coupon }) => (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3 mb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Percent className="w-5 h-5 text-purple-600" />
          </div>
          
          <div>
            <h5 className="font-medium text-gray-900">{coupon.title}</h5>
            <p className="text-sm text-gray-600">{coupon.description}</p>
            
            <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>Expires {new Date(coupon.validUntil).toLocaleDateString()}</span>
              </div>
              
              <div className="flex items-center space-x-1">
                <Users className="w-3 h-3" />
                <span>{coupon.timesUsed} used</span>
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => handleSponsorInteraction(coupon.sponsorId, 'coupon_claim', { couponId: coupon.id })}
          className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 transition-colors flex items-center space-x-1"
        >
          <Gift className="w-3 h-3" />
          <span>Claim</span>
        </button>
      </div>
    </div>
  );

  if (context === 'recipe') {
    return (
      <div className={className}>
        {/* Example Data Disclaimer */}
        {isExampleData && (
          <DataDisclaimer 
            type="info"
            title="Example Sponsorship"
            message="These are example sponsored products and offers for demonstration. Real sponsorships will be available when you connect with local businesses."
            className="mb-4"
          />
        )}

        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <Award className="w-4 h-4 mr-2 text-green-600" />
            Community Recommended Ingredients
            {isExampleData && <MockBadge variant="small" className="ml-2" />}
          </h4>
          {featuredProducts.slice(0, 2).map(product => (
            <ProductSuggestion key={product.id} product={product} />
          ))}
        </div>

        {availableCoupons.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
              <Tag className="w-4 h-4 mr-2 text-purple-600" />
              Special Offers for Ingredients
            </h4>
            {availableCoupons.slice(0, 1).map(coupon => (
              <CouponOffer key={coupon.id} coupon={coupon} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default SponsorshipIntegration;
